import React, { useMemo, useState } from 'react';
import { StepProps, Specialty, SPECIALTY_COLORS } from '../../types';
import { motion, AnimatePresence } from 'framer-motion';
import { Droplets, Heart, FlaskConical, Stethoscope, ArrowRight, MessageCircle } from 'lucide-react';
import { playSuccess, playWrong } from '../../utils/sounds';
import { Celebration } from '../Celebration';
import { shuffle } from '../../utils/shuffle';
export function QuizScreen({
  state,
  updateState,
  nextStep
}: StepProps) {
  const [selected, setSelected] = useState<Specialty | null>(state.quizAnswer);
  const [showFeedback, setShowFeedback] = useState(!!state.quizAnswer);
  const [celebration, setCelebration] = useState<'success' | 'wrong' | null>(null);
  const specialties = useMemo(() => shuffle([{
    id: 'Nephrologist' as Specialty,
    icon: Droplets,
    label: 'Nephrologist'
  }, {
    id: 'Cardiologist' as Specialty,
    icon: Heart,
    label: 'Cardiologist'
  }, {
    id: 'Endocrinologist' as Specialty,
    icon: FlaskConical,
    label: 'Endocrinologist'
  }, {
    id: 'PCP' as Specialty,
    icon: Stethoscope,
    label: 'PCP'
  }]), []);
  const handleSelect = (id: Specialty) => {
    if (showFeedback) return;
    setSelected(id);
    const isCorrect = id === 'Cardiologist';
    updateState({
      quizAnswer: id
    });
    setTimeout(() => {
      setShowFeedback(true);
      if (isCorrect) {
        playSuccess();
        setCelebration('success');
      } else {
        playWrong();
        setCelebration('wrong');
      }
      setTimeout(() => setCelebration(null), 2000);
    }, 500);
  };
  const getFeedback = () => {
    switch (selected) {
      case 'Nephrologist':
        return 'Surprising insight: Nephrologists often adopt a more cautious, reactive approach due to late-stage referrals and safety concerns.';
      case 'Cardiologist':
        return "You're right — cardiologists tend to be among the most proactive, embracing early multi-modal treatment.";
      case 'Endocrinologist':
        return 'Close — Endocrinologists are proactive but primarily through a metabolic lens, often deferring non-glycaemic treatment.';
      case 'PCP':
        return 'PCPs manage early-stage CKD but tend to adopt conservative approaches and refer for escalation.';
      default:
        return '';
    }
  };
  return <div className="flex-grow flex flex-col justify-center items-center px-6 py-12 min-h-screen pt-20" data-id="element-647">
      <Celebration type={celebration} data-id="element-648" />
      <div className="max-w-3xl w-full mx-auto" data-id="element-649">
        <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 mb-8 flex items-start" data-id="element-650">
          <MessageCircle className="text-blue-500 mr-3 mt-0.5 flex-shrink-0" size={20} data-id="element-651" />
          <p className="text-blue-800 font-medium" data-id="element-652">
            Before we split into groups — discuss at your table:
          </p>
        </div>

        <h2 className="text-3xl md:text-4xl font-bold mb-10 text-center" data-id="element-653">
          Which specialty do you think is{' '}
          <span className="text-brand" data-id="element-654">MOST proactive</span> in managing CKD?
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8" data-id="element-655">
          {specialties.map(spec => {
          const Icon = spec.icon;
          const isSelected = selected === spec.id;
          const isFaded = showFeedback && !isSelected;
          return <motion.button key={spec.id} whileHover={!showFeedback ? {
            scale: 1.02
          } : {}} whileTap={!showFeedback ? {
            scale: 0.98
          } : {}} onClick={() => handleSelect(spec.id)} className={`
                  relative p-6 rounded-2xl border-2 text-left flex items-center transition-all duration-300
                  ${isSelected ? `border-[${SPECIALTY_COLORS[spec.id]}] bg-gray-50` : 'border-gray-200 hover:border-gray-300 bg-white'}
                  ${isFaded ? 'opacity-50 grayscale' : 'opacity-100'}
                `} style={{
            borderColor: isSelected ? SPECIALTY_COLORS[spec.id] : undefined
          }} data-id="element-656">
                <div className={`p-4 rounded-full mr-4`} style={{
              backgroundColor: `${SPECIALTY_COLORS[spec.id]}15`,
              color: SPECIALTY_COLORS[spec.id]
            }} data-id="element-657">
                  <Icon size={28} data-id="element-658" />
                </div>
                <span className="text-xl font-semibold" data-id="element-659">{spec.label}</span>
              </motion.button>;
        })}
        </div>

        <AnimatePresence data-id="element-660">
          {showFeedback && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="mt-8" data-id="element-661">
              <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6 mb-8" data-id="element-662">
                <p className="text-lg text-gray-800 leading-relaxed font-medium" data-id="element-663">
                  {getFeedback()}
                </p>
              </div>
              <div className="flex justify-center" data-id="element-664">
                <button onClick={nextStep} className="inline-flex items-center justify-center px-8 py-3 text-base font-semibold text-white bg-dark rounded-full hover:bg-black transition-colors" data-id="element-665">
                  Continue
                  <ArrowRight className="ml-2 w-5 h-5" data-id="element-666" />
                </button>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}